import React from 'react';
import { renderMarkdown } from './markdownRenderer';

const GOLD = '#D4A417';
const BORDER = '#252a38';
const SURFACE = '#13161e';

// Spec/RFI docs get a green dot, drawing docs get a blue dot.
function dotColorForDocType(docType) {
  const t = (docType || '').toLowerCase();
  if (t.includes('drawing')) return '#3b82f6'; // blue
  return '#22c55e'; // green
}

// Best-effort relative age from whatever timestamp the doc carries.
function formatAge(doc) {
  const raw = doc && (doc.age || doc.createdAt || doc.uploadedAt || doc.lastAccessedAt);
  if (!raw) return null;
  // If a pre-formatted age string was provided, use it as-is.
  if (typeof raw === 'string' && /ago|just now/i.test(raw)) return raw;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Decode a base64 PDF and trigger a browser download.
function downloadPdf(pdfBase64, filename) {
  const byteChars = atob(pdfBase64);
  const byteArr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'Report.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

function formatTimestamp(ts) {
  const d = ts ? new Date(ts) : new Date();
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const TAB_DESCRIPTIONS = {
  'spec-check': 'Upload a specification and submittal — AW Manager will cross-reference and flag every compliance gap.',
  'spec-builder': 'Upload a master spec and template to generate a complete specification outline.',
  'spec-update': 'Upload a spec sheet to check for discontinued products, updated models, and code changes.',
  'rfi-check': 'Upload an RFI and project docs — AW Manager will analyze and draft formal responses.',
  'shop-drawing-check': 'Upload any submittal package — shop drawings, cut sheets, schedules. AW Manager cross-references between files and flags red flags.',
  'design-vs-shop': 'Upload design/contract drawings and shop drawings — AW Manager identifies every deviation from design intent.',
  'drawing-compare': 'Upload two drawing versions — AW Manager identifies every change between revisions.',
  'track-changes': 'Upload a master spec and customer spec to identify gaps and missing requirements.',
};

const TAB_LABELS = {
  'spec-check':         'Spec Check',
  'spec-builder':       'Spec Builder',
  'spec-update':        'Spec Update',
  'rfi-check':          'RFI Check',
  'shop-drawing-check': 'Package Check',
  'design-vs-shop':     'Design vs Shop',
  'drawing-compare':    'Drawing Compare',
  'track-changes':      'Track Changes',
};

function ResultsPanel({ result, activeTab, onClear, cachedDocs, vaultDrawings, isRunning = false }) {
  const docs = Array.isArray(cachedDocs) ? cachedDocs : [];
  const drawings = Array.isArray(vaultDrawings) ? vaultDrawings : [];

  const panelStyle = {
    height: 'calc(100vh - 52px)',
    width: '100%',
    boxSizing: 'border-box',
    background: '#0d0f14',
    borderRadius: '16px',
    border: `1px solid ${BORDER}`,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#252a38 transparent',
    fontFamily: 'sans-serif',
    color: '#e8eaf0',
  };

  const scrollbarCss = `
    .zt-results-panel::-webkit-scrollbar { width: 6px; }
    .zt-results-panel::-webkit-scrollbar-track { background: #13161e; }
    .zt-results-panel::-webkit-scrollbar-thumb { background: #252a38; border-radius: 4px; }
    .zt-results-panel::-webkit-scrollbar-thumb:hover { background: #D4A417; }
    .zt-cached-scroll::-webkit-scrollbar { width: 4px; }
    .zt-cached-scroll::-webkit-scrollbar-track { background: transparent; }
    .zt-cached-scroll::-webkit-scrollbar-thumb { background: #252a38; border-radius: 4px; }
    @keyframes zt-ring { 0% { transform: scale(0.55); opacity: 0.8; } 100% { transform: scale(2.6); opacity: 0; } }
    @keyframes zt-core { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.88); opacity: 0.55; } }
    @keyframes zt-dot  { 0%,80%,100% { transform: translateY(0); opacity: 0.25; } 40% { transform: translateY(-7px); opacity: 1; } }
    @keyframes zt-bar  { 0% { left: -40%; } 100% { left: 110%; } }
  `;

  // ===== RUNNING STATE (no prior result) =====
  if (isRunning && !result) {
    const label = TAB_LABELS[activeTab] || 'Analysis';
    return (
      <div style={panelStyle} className="zt-results-panel">
        <style>{scrollbarCss}</style>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
          {/* Pulsing rings */}
          <div style={{ position: 'relative', width: '88px', height: '88px', marginBottom: '32px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: `2px solid ${GOLD}`,
                animation: `zt-ring 2.1s ease-out infinite`,
                animationDelay: `${i * 0.7}s`,
                opacity: 0,
              }} />
            ))}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                width: '26px', height: '26px', borderRadius: '50%',
                background: GOLD,
                animation: 'zt-core 1.6s ease-in-out infinite',
              }} />
            </div>
          </div>
          {/* Tool label */}
          <div style={{ fontSize: '11px', fontWeight: '700', color: GOLD, letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '10px' }}>
            {label}
          </div>
          <div style={{ fontSize: '15px', color: '#7a8099', marginBottom: '22px' }}>
            Running analysis
          </div>
          {/* Bouncing dots */}
          <div style={{ display: 'flex', gap: '7px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: '7px', height: '7px', borderRadius: '50%',
                background: GOLD,
                animation: 'zt-dot 1.3s ease-in-out infinite',
                animationDelay: `${i * 0.22}s`,
              }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ===== EMPTY STATE =====
if (!result) {
  return (
    <div style={panelStyle} className="zt-results-panel">
      <style>{scrollbarCss}</style>
        <div
          style={{
            flex: 1,
            minHeight: '100%',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 32px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '0.5px', color: '#e8eaf0' }}>
            AW <span style={{ color: GOLD }}>AI Tools</span>
          </div>
<div style={{ fontSize: '15px', color: '#7a8099', marginTop: '12px', maxWidth: '420px', lineHeight: 1.5 }}>
  {TAB_DESCRIPTIONS[activeTab] || 'Attach documents in the manager and describe what you need →'}
</div>

          {docs.length > 0 && (
            <div style={{ width: '100%', maxWidth: '520px', marginTop: '36px', textAlign: 'left' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#7a8099', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                Cached documents
              </div>
<div className="zt-cached-scroll" style={{ 
  display: 'flex', flexDirection: 'column', gap: '8px',
  maxHeight: '240px', overflowY: 'auto',
  scrollbarWidth: 'thin', scrollbarColor: '#1e2235 transparent'
}}>

                {docs.map((doc, i) => {
                  const age = formatAge(doc);
                  return (
                    <div
                      key={(doc && doc.name) ? `${doc.name}-${i}` : i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        background: SURFACE,
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: dotColorForDocType(doc && doc.docType),
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0, fontSize: '13px', color: '#e8eaf0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(doc && doc.name) || 'Untitled'}
                      </span>
                      {age && (
                        <span style={{ fontSize: '11px', color: '#7a8099', flexShrink: 0 }}>{age}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

{drawings.length > 0 && (
            <div style={{ width: '100%', maxWidth: '520px', marginTop: '24px', textAlign: 'left' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#5b627a', letterSpacing: '0.08em', marginBottom: '12px' }}>
                DRAWING VAULT
              </div>
              <div className="zt-cached-scroll" style={{ maxHeight: '200px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#1e2235 transparent' }}>
                {drawings.map((d, i) => (
                  <div key={d.id || i} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 14px', marginBottom: '8px',
                    background: SURFACE, border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                  }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: '#e8eaf0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.fileName}
                      </div>
                      {(d.sheetNumber || d.revisionNumber) && (
                        <div style={{ fontSize: '11px', color: '#7a8099', marginTop: '2px' }}>
                          {d.sheetNumber ? `Sheet ${d.sheetNumber}` : ''}{d.sheetNumber && d.revisionNumber ? ' · ' : ''}{d.revisionNumber ? `Rev ${d.revisionNumber}` : ''}
                        </div>
                      )}
                    </div>
                    {d.daysLeft != null && (
                      <div style={{ fontSize: '10px', color: '#5b627a' }}>{d.daysLeft}d left</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== RESULT STATE =====
return (
  <div style={panelStyle} className="zt-results-panel">
    <style>{scrollbarCss}</style>
    {/* Slim animated bar shown while a new analysis runs on top of an existing result */}
    {isRunning && (
      <div style={{ position: 'relative', height: '3px', background: '#1e2235', flexShrink: 0, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', height: '100%', width: '40%',
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          animation: 'zt-bar 1.4s ease-in-out infinite',
        }} />
      </div>
    )}
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '20px 28px',
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}
      >

{onClear && (
  <button
    onClick={onClear}
    style={{
      flexShrink: 0,
      background: 'transparent', border: `1px solid ${BORDER}`,
      color: '#7a8099', borderRadius: '6px', padding: '9px 14px',
      fontSize: '12px', cursor: 'pointer', fontFamily: 'sans-serif'
    }}
  >
    ✕ Clear
  </button>
)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '18px', fontWeight: '800', color: '#e8eaf0' }}>
            {result.toolLabel || 'Report'}
          </div>
          <div style={{ fontSize: '12px', color: '#7a8099', marginTop: '2px' }}>
            {formatTimestamp(result.timestamp)}
          </div>
        </div>
<div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
  {result.pdfBase64 && (
    <button
      type="button"
      onClick={() => downloadPdf(result.pdfBase64, result.filename)}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '10px 18px', borderRadius: '8px', border: 'none',
        background: GOLD, color: '#13161e', fontSize: '13px',
        fontWeight: '700', cursor: 'pointer', fontFamily: 'sans-serif',
      }}
    >
      ⬇ PDF
    </button>
  )}
  {result.report && (
    <button
      type="button"
      onClick={async () => {
        const API_BASE = 'https://apispeccheck.zonetap.com';
        try {
          const res = await fetch(`${API_BASE}/api/manager-tools/export-docx`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ report: result.report, toolLabel: result.toolLabel })
          });
          const data = await res.json();
          if (!data.docxBase64) return;
          const byteChars = atob(data.docxBase64);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          const blob = new Blob([byteArr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename || `Report_${new Date().toISOString().slice(0,10)}.docx`;
          a.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Word export error:', err);
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '10px 18px', borderRadius: '8px',
        border: '1px solid #6b7db3', background: 'transparent',
        color: '#6b7db3', fontSize: '13px',
        fontWeight: '700', cursor: 'pointer', fontFamily: 'sans-serif',
      }}
    >
      ⬇ Word
    </button>
  )}
</div>
      </div>

      {/* Scrollable report body */}
      {result.pdfBase64 ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <iframe
            src={`data:application/pdf;base64,${result.pdfBase64}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: '#fff'
            }}
            title={result.toolLabel}
          />
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 28px',
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#e8eaf0',
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: renderMarkdown(result.report) }} />
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 28px',
          borderTop: `1px solid ${BORDER}`,
          fontSize: '11px',
          color: '#7a8099',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Generated by AECWorks AI Tools</span>
        <span>{formatTimestamp(result.timestamp)}</span>
      </div>
    </div>
  );
}

export default ResultsPanel;
